import { CrossroadsEntityType } from '@/api/types'
import { useDebouncedSearch } from '@/pages/Dashboard/panels/ReferenceDataPanel/hooks/useDebouncedSearch'
import { useEntities } from '@/pages/Dashboard/panels/ReferenceDataPanel/hooks/useEntities'
import { BBDTag } from '@gravitate-js/excalibrr'
import { Select } from 'antd'
import { useMemo, useState } from 'react'

interface MRDEntitySelectProps {
  value?: string | string[]
  onChange?: (value: string | string[] | undefined) => void
  entityType: CrossroadsEntityType | CrossroadsEntityType[]
  multi?: boolean
  showTypeSelector?: boolean
  autoFocus?: boolean
  defaultOpen?: boolean
  forcedSearchTerm?: string
  width?: number
  height?: number
}

const entityTypeOptions = [
  { value: 'Terminal', label: 'Terminal' },
  { value: 'Site', label: 'Site' },
  { value: 'Product', label: 'Product' },
  { value: 'Tank', label: 'Tank' },
  { value: 'Company', label: 'Company' },
  { value: 'Tenant', label: 'Tenant' },
]

export function MRDEntitySelect({
  value,
  onChange,
  entityType: defaultEntityType,
  multi = false,
  showTypeSelector = false,
  autoFocus = false,
  defaultOpen = false,
  forcedSearchTerm,
  width,
  height,
}: MRDEntitySelectProps) {
  const [type, setType] = useState<CrossroadsEntityType>(
    Array.isArray(defaultEntityType) ? defaultEntityType[0] : defaultEntityType,
  )
  const { searchTerm, setSearchTerm } = useDebouncedSearch()

  const resolvedSearchTerm = forcedSearchTerm ?? searchTerm

  const resolvedType = Array.isArray(defaultEntityType)
    ? defaultEntityType
    : type
  const { data: entities, isLoading } = useEntities(
    resolvedSearchTerm,
    resolvedType,
  )

  const options = useMemo(() => {
    if (!entities) return []
    return entities
      .filter((e) => e.goid)
      .map((e) => ({
        value: e.goid,
        label: `${e.name}`,
        goid: e.goid,
        data: e,
      }))
  }, [entities])

  // Normalize value for the Select component
  const selectValue = multi
    ? Array.isArray(value)
      ? value
      : value
        ? [value]
        : []
    : Array.isArray(value)
      ? value[0]
      : value

  const handleChange = (newValue: string | string[]) => {
    if (multi) {
      onChange?.(
        Array.isArray(newValue) ? newValue : newValue ? [newValue] : [],
      )
    } else {
      const single = Array.isArray(newValue) ? newValue[0] : newValue
      onChange?.(single || undefined)
    }
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <Select
        mode={multi ? 'multiple' : undefined}
        value={selectValue}
        onChange={handleChange}
        loading={isLoading}
        placeholder={multi ? 'Select MRD Entities' : 'Select MRD Entity'}
        showSearch={!forcedSearchTerm}
        allowClear
        autoFocus={autoFocus}
        defaultOpen={defaultOpen}
        style={{ width: width ?? '100%', minWidth: 200, height }}
        notFoundContent={isLoading ? 'Loading...' : 'No entities found'}
        filterOption={false}
        tagRender={
          multi
            ? (tagProps) => (
                <BBDTag
                  theme2
                  style={{ margin: '2px' }}
                  closable={tagProps.closable}
                  onClose={tagProps.onClose}
                >
                  {tagProps.label}
                </BBDTag>
              )
            : undefined
        }
        optionRender={(option) => (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <BBDTag theme2 style={{ margin: 0 }}>
              {option.data.label}
            </BBDTag>
            <span style={{ color: '#8c8c8c', fontSize: '12px' }}>
              ({option.data.goid})
            </span>
          </div>
        )}
        options={[...options]}
        virtual={false}
        placement='bottomLeft'
        popupMatchSelectWidth={false}
        getPopupContainer={() => document.body}
        dropdownStyle={{
          minWidth: 300,
          maxWidth: 500,
        }}
        onSearch={setSearchTerm}
      />
      {showTypeSelector && (
        <Select
          options={entityTypeOptions}
          style={{ width: '150px' }}
          value={type}
          onChange={(e) => setType(e)}
          dropdownStyle={{
            minWidth: 150,
          }}
        />
      )}
    </div>
  )
}
